<!-- eslint-disable no-undef -->
<script setup>
import { ref, onMounted } from 'vue';
import JsonEditor from '@/components/common/JsonEditor.vue';
import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

const props = defineProps({
    app:         { type: Object,   required: true },
    deviceId:    { type: String,   required: true },
    sendAction:  { type: Function, required: true },
    awaitResult: { type: Function, required: true },
});

const emit = defineEmits(['close']);

const configIds = ref([]);
const activeConfigId = ref(null);
const configValues = ref({});   // keyed by config_id
const loadedTabs = ref({});     // keyed by config_id → true once fetched
const manifestLoading = ref(true);
const manifestError = ref(null);
const tabLoading = ref(false);
const tabError = ref(null);
const saveError = ref(null);
const saving = ref(false);

const closeModal = () => emit('close');
const modalBackdrop = useModalBackdropClose(closeModal);

onMounted(async () => {
    await fetchManifest();
});

const fetchManifest = async () => {
    manifestLoading.value = true;
    manifestError.value = null;
    try {
        const { ok, action_id } = await props.sendAction({
            action_class: 'app',
            action: 'manifest',
            action_type: 'Read',
            payload: { app_id: props.app.app_id },
        });
        if (!ok) throw new Error('Failed to fetch manifest');
        const result = await props.awaitResult(action_id);
        const files = result?.config_files ?? [];
        configIds.value = files.map(f => (typeof f === 'string' ? f : f.config_id ?? f.id ?? String(f)));
        if (configIds.value.length > 0) {
            activeConfigId.value = configIds.value[0];
            await fetchConfig(activeConfigId.value);
        }
    } catch (e) {
        manifestError.value = e.message || 'Failed to load manifest';
    } finally {
        manifestLoading.value = false;
    }
};

const fetchConfig = async (configId) => {
    if (loadedTabs.value[configId]) return;
    tabLoading.value = true;
    try {
        const { ok, action_id } = await props.sendAction({
            action_class: 'config',
            action: 'read',
            action_type: 'Read',
            payload: { app_id: props.app.app_id, config_id: configId },
        });
        if (!ok) throw new Error('Failed to load config');
        const result = await props.awaitResult(action_id);
        configValues.value = { ...configValues.value, [configId]: result ?? {} };
        loadedTabs.value = { ...loadedTabs.value, [configId]: true };
    } catch (e) {
        configValues.value = { ...configValues.value, [configId]: {} };
        loadedTabs.value = { ...loadedTabs.value, [configId]: true };
        tabError.value = e.message || 'Failed to load config';
    } finally {
        tabLoading.value = false;
    }
};

const selectTab = async (configId) => {
    activeConfigId.value = configId;
    tabError.value = null;
    await fetchConfig(configId);
};

const retryFetchConfig = async () => {
    const configId = activeConfigId.value;
    if (!configId) return;
    tabError.value = null;
    loadedTabs.value = { ...loadedTabs.value };
    delete loadedTabs.value[configId];
    await fetchConfig(configId);
};

const updateConfigValue = (configId, newValue) => {
    configValues.value = { ...configValues.value, [configId]: newValue };
};

const saveConfig = async () => {
    if (!activeConfigId.value) return;
    saving.value = true;
    saveError.value = null;
    try {
        const { ok, action_id } = await props.sendAction({
            action_class: 'config',
            action: 'update',
            action_type: 'Update',
            payload: {
                app_id: props.app.app_id,
                config_id: activeConfigId.value,
                config: configValues.value[activeConfigId.value] ?? {},
            },
        });
        if (!ok) throw new Error('Failed to save config');
        await props.awaitResult(action_id);
        emit('close');
    } catch (e) {
        saveError.value = e.message || 'Failed to save config';
    } finally {
        saving.value = false;
    }
};

defineExpose({ configValues, saveConfig, saveError, tabError, retryFetchConfig });
</script>

<template>
    <div
        class="fixed inset-0 bg-mc-ink/30 flex justify-center items-center z-50"
        @mousedown.self="modalBackdrop.onBackdropMousedown"
        @mouseup.self="modalBackdrop.onBackdropMouseup"
    >
        <div
            class="bg-mc-white dark:bg-mc-charcoal rounded-mc-md shadow-mc-md w-[560px] max-h-[80vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="`config-modal-title-${app.app_id}`"
            tabindex="-1"
            @mousedown="modalBackdrop.onContentMousedown"
            @keydown.esc="closeModal"
        >
            <!-- Header -->
            <div class="flex justify-between items-start p-4 border-b border-mc-steel dark:border-line-on-dark flex-shrink-0">
                <div>
                    <h3 :id="`config-modal-title-${app.app_id}`" class="font-display font-black text-tier-4 text-mc-ink dark:text-on-dark">
                        Edit Configuration — {{ app.name }}
                    </h3>
                    <p class="font-mono text-tier-1 text-ink-muted dark:text-on-dark-muted mt-0.5">{{ app.app_id }}</p>
                </div>
                <button
                    type="button"
                    class="text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark transition-colors duration-160 text-2xl leading-none focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                    aria-label="Close"
                    @click="closeModal"
                >×</button>
            </div>

            <!-- Loading manifest -->
            <div v-if="manifestLoading" class="flex-1 flex flex-col items-center justify-center gap-3 p-8">
                <LoadingAnimation3 size="36px" />
                <p class="font-body text-sm text-ink-muted dark:text-on-dark-muted">Contacting unit for configuration…</p>
            </div>

            <!-- Manifest error -->
            <div v-else-if="manifestError" class="flex-1 flex flex-col items-center justify-center gap-3 p-8">
                <p class="font-body text-tier-3 text-mc-red">{{ manifestError }}</p>
                <button
                    type="button"
                    class="btn-primary text-sm"
                    @click="fetchManifest"
                >Retry</button>
            </div>

            <!-- No config files -->
            <div v-else-if="configIds.length === 0" class="flex-1 flex items-center justify-center p-8">
                <p class="font-body text-tier-3 text-ink-muted dark:text-on-dark-muted">No configuration files defined for this app.</p>
            </div>

            <!-- Config tabs + editor -->
            <template v-else>
                <!-- Config ID tabs (only shown when multiple config files) -->
                <div
                    v-if="configIds.length > 1"
                    class="flex border-b border-mc-steel dark:border-line-on-dark flex-shrink-0 px-4 bg-mc-cloud dark:bg-mc-charcoal"
                >
                    <button
                        v-for="configId in configIds"
                        :key="configId"
                        type="button"
                        class="px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                        :class="activeConfigId === configId
                            ? 'border-mc-red text-mc-ink dark:text-on-dark'
                            : 'border-transparent text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark'"
                        @click="selectTab(configId)"
                    >{{ configId }}</button>
                </div>

                <!-- Editor body -->
                <div class="flex-1 overflow-y-auto p-4">
                    <div v-if="tabLoading" class="flex flex-col items-center justify-center gap-3 py-8">
                        <LoadingAnimation3 size="36px" />
                        <p class="font-body text-sm text-ink-muted dark:text-on-dark-muted">Contacting unit for configuration…</p>
                    </div>
                    <div v-else-if="tabError" class="flex flex-col items-center justify-center py-8 gap-3">
                        <p class="font-body text-tier-3 text-mc-red">{{ tabError }}</p>
                        <button type="button" class="btn-primary text-sm" @click="retryFetchConfig">Retry</button>
                    </div>
                    <JsonEditor
                        v-else-if="activeConfigId && configValues[activeConfigId] !== undefined"
                        :jsonValue="configValues[activeConfigId]"
                        :advanced="true"
                        @update:jsonValue="updateConfigValue(activeConfigId, $event)"
                    />
                </div>

                <!-- Save error -->
                <div v-if="saveError" class="px-4 pb-2 flex-shrink-0">
                    <p class="font-body text-tier-2 text-mc-red">{{ saveError }}</p>
                </div>

                <!-- Footer -->
                <div class="flex justify-end gap-2 p-4 border-t border-mc-steel dark:border-line-on-dark bg-mc-cloud dark:bg-mc-charcoal flex-shrink-0">
                    <button
                        type="button"
                        class="px-4 py-2 text-sm font-semibold rounded-mc-sm border border-mc-steel dark:border-line-on-dark text-mc-ink dark:text-on-dark hover:bg-mc-steel/30 dark:hover:bg-white/10 transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                        @click="closeModal"
                    >Cancel</button>
                    <button
                        type="button"
                        class="btn-primary px-4 py-2 text-sm"
                        :disabled="saving"
                        @click="saveConfig"
                    >{{ saving ? 'Saving…' : 'Save Config' }}</button>
                </div>
            </template>
        </div>
    </div>
</template>
